import React, { useState, useEffect, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Header from './Header'
import Sidebar from './Sidebar'
import RightDrawer from './RightDrawer'
import HelpSidebar from './HelpSidebar'
import SupportTicketModal from './SupportTicketModal'
import RouteErrorBoundary from './RouteErrorBoundary'
import CompleteProfileModal from './CompleteProfileModal'
import TourMenu from '../tours/TourMenu'
import { isProfileComplete } from '../utils/businessLevelLabels'
import TourResumeEffect from '../tours/TourResumeEffect'
import { parseUserGuideMarkdown, HELP_TOC } from '../tours/configureMarked'
import { startGuidedTour } from '../tours/runGuidedTour'
import type { TourId } from '../tours/tourIds'
import { useAuth } from '../context/AuthContext'
import { fetchMetrics, fetchCheckRuns } from '../services/api'
import { isActiveRunStatus } from '../services/complianceV2'
import './Layout.css'


interface LayoutProps {
  children: React.ReactNode
}
export default function Layout({ children }: LayoutProps) {

  const { instance, accounts } = useMsal()
  const { user: authUser, logout: authLogout, stopImpersonation, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [helpContent, setHelpContent] = useState<string>('Loading...')
  const [tourMenuKey, setTourMenuKey] = useState(0)

  useEffect(() => {
    fetch('/user-guide.md')
      .then(res => res.text())
      .then((md) => {
        setHelpContent(parseUserGuideMarkdown(md))
      })
      .catch(() => setHelpContent('<p>User guide not found.</p>'))
  }, [])

  const user = authUser ? null : accounts[0]
  const userName = authUser?.displayName ?? user?.name ?? 'Guest User'
  const userEmail = authUser?.email ?? user?.username ?? ''

  const { data: metrics } = useQuery({
    queryKey: ['metrics'],
    queryFn: fetchMetrics,
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 30000 : false,
  })

  const { data: recentRuns } = useQuery({
    queryKey: ['check-runs', 'active'],
    // V2 runs use Queued/Acquiring/Extracting/… — not only legacy InProgress.
    queryFn: () => fetchCheckRuns({ take: 50 }),
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 10000 : false,
  })

  const notificationCount = metrics?.overdueActions ?? 0
  const processCount = recentRuns?.filter((run) => isActiveRunStatus(run.status)).length ?? 0

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleMenuToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  const handleProfileClick = () => {
    setDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }

  const handleLogout = async () => {
    const isSsoSession =
      authUser?.authMethod === 'sso' || (!authUser && instance.getAllAccounts().length > 0)

    if (isSsoSession) {
      sessionStorage.setItem('mimgrc_sso_logout', 'true')
    } else {
      sessionStorage.removeItem('mimgrc_sso_logout')
    }

    authLogout()
    setDrawerOpen(false)

    if (!isSsoSession) {
      navigate('/login', { replace: true })
      return
    }

    try {
      const account = instance.getAllAccounts()[0]
      await instance.logoutRedirect(
        account
          ? ({ account, postLogoutRedirectUri: window.location.origin + '/login' } as Parameters<typeof instance.logoutRedirect>[0])
          : ({ postLogoutRedirectUri: window.location.origin + '/login' } as Parameters<typeof instance.logoutRedirect>[0]),
      )
    } catch (error) {
      console.error('Logout failed:', error)
      navigate('/login', { replace: true })
    }
  }

  const handleSettings = () => {
    setDrawerOpen(false)
    navigate('/settings')
  }

  const handleNavigation = () => {
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true)
    }
  }

  const handleTourComplete = useCallback(() => {
    setTourMenuKey((k) => k + 1)
  }, [])

  const handleStartTour = useCallback(
    (tourId: TourId) => {
      setHelpOpen(false)
      void startGuidedTour({
        tourId,
        navigate,
        pathname: location.pathname,
        search: location.search,
        onComplete: handleTourComplete,
      })
    },
    [navigate, location.pathname, location.search, handleTourComplete],
  )

  return (
    <div className="app-layout">
      <TourResumeEffect onTourComplete={handleTourComplete} />

      <Header
        onMenuToggle={handleMenuToggle}
        onProfileClick={handleProfileClick}
        onHelpClick={() => setHelpOpen(true)}
        onSupportClick={() => setSupportOpen(true)}
        isSidebarCollapsed={sidebarCollapsed}
        notificationCount={notificationCount}
        processCount={processCount}
        userName={userName}
        userCompany="Kingspan"
      />

      <Sidebar
        isCollapsed={sidebarCollapsed}
        onNavigate={handleNavigation}
      />

      <main className={`app-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {authUser?.isImpersonating && (
          <div
            style={{
              background: '#eff6ff',
              borderBottom: '1px solid #93c5fd',
              padding: '0.6rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              fontSize: '0.875rem',
              color: '#1e40af',
              flexWrap: 'wrap',
            }}
          >
            <span>
              <strong>Viewing as {authUser.displayName}</strong>
              {authUser.impersonatorDisplayName && (
                <> — signed in as {authUser.impersonatorDisplayName} for support</>
              )}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                if (stopImpersonation()) navigate('/users')
                else authLogout()
              }}
            >
              Exit impersonation
            </button>
          </div>
        )}
        <div className="main-content">
          <RouteErrorBoundary resetKey={location.pathname}>
            {children}
          </RouteErrorBoundary>
        </div>
      </main>

      {authUser && !authUser.isImpersonating && !isProfileComplete(authUser) && (
        <CompleteProfileModal open />
      )}

      <RightDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        userName={userName}
        userEmail={userEmail}
        userCompany="Kingspan"
        userRole={authUser?.role ?? 'User'}
        userId={authUser?.userId}
        businessLevel={authUser?.businessLevel}
        functionalRoles={authUser?.functionalRoles}
        divisionName={authUser?.divisionName}
        subdivisionName={authUser?.subdivisionName}
        businessEntityNames={authUser?.businessEntityNames}
        entraObjectId={authUser?.entraObjectId}
        entraTenantId={authUser?.entraTenantId}
        entraPreferredUsername={authUser?.entraPreferredUsername}
        entraName={authUser?.entraName}
        entraIdentityProvider={authUser?.entraIdentityProvider}
        onLogout={handleLogout}
        onSettings={handleSettings}
      />

      <HelpSidebar
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        toc={HELP_TOC}
        content={<div dangerouslySetInnerHTML={{ __html: helpContent }} />}
        tours={<TourMenu key={tourMenuKey} onStartTour={handleStartTour} />}
      />

      <SupportTicketModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  )
}
